import { sound } from '@pixi/sound';
import { Container, Sprite, Texture, VideoResource } from 'pixi.js';
import { CutScene, Store, TextureFactory } from '../../core';

interface CutTexture {
  type: 'IMAGE' | 'VIDEO';
  src: string;
}

interface CutSprite {
  audio?: string;
  isAudioPlaying: boolean;
  type: 'IMAGE' | 'VIDEO';
  sprite: Sprite;
}

class Cut extends CutScene {
  private stage: Container;
  public cutSprites: Array<CutSprite> = [];
  public cutIndex: number = 0;
  private videos: Array<HTMLVideoElement> = [];
  private timer: number = 100;

  constructor(
    cutInfos: Array<{
      audio?: string;
      texture: CutTexture;
    }>,
    scale: number = 2
  ) {
    super();
    this.stage = new Container();
    this.addChild(this.stage);

    for (const { texture, audio } of cutInfos) {
      const cut = new Sprite(Texture.EMPTY);

      if (texture.type === 'VIDEO') {
        const video: HTMLVideoElement = document.createElement('video');

        video.src = texture.src;
        video.muted = true;
        video.playsInline = true;
        video.autoplay = false;
        video.preload = 'auto';

        video.pause();

        this.videos.push(video);

        cut.texture = Texture.from(video);
        (cut.texture.baseTexture.resource as any).autoPlay = false;
        (cut.texture.baseTexture.resource as VideoResource).source.pause();
      } else {
        cut.texture = TextureFactory.instance.getSingleTexture(texture.src);
      }

      cut.scale.set(1 / scale);
      cut.anchor.set(0.5);
      cut.alpha = 0;

      this.cutSprites.push({
        audio,
        isAudioPlaying: false,
        type: texture.type,
        sprite: cut,
      });
      this.stage.addChild(cut);
    }
  }

  public setTimer(timer: number): void {
    const currentCut = this.cutSprites[this.cutIndex];
    if (currentCut.sprite.alpha >= 0.8) {
      this.timer = timer;
    }
  }

  public next(): void {
    const currentCut = this.cutSprites[this.cutIndex];

    if (currentCut.sprite.alpha >= 0.8) {
      if (this.cutIndex < this.cutSprites.length - 1) {
        if (currentCut.type === 'VIDEO') {
          (currentCut.sprite.texture.baseTexture.resource as VideoResource).source.pause();
        }
        this.cutIndex++;
        this.timer = 100;
      } else {
        if (currentCut.type === 'VIDEO') {
          (currentCut.sprite.texture.baseTexture.resource as VideoResource).source.pause();
        }
        this.emit('sceneFinished');
      }
    }
  }

  public update(elapsed: number): void {
    const currentCut = this.cutSprites[this.cutIndex];

    if (currentCut.sprite.alpha < 1 - 0.02 * elapsed) {
      currentCut.sprite.alpha += 0.02 * elapsed;
    } else {
      currentCut.sprite.alpha = 1;

      if (currentCut.audio && !currentCut.isAudioPlaying) {
        sound.play(currentCut.audio, { loop: false, volume: Store.instance.state.volume as number });
        currentCut.isAudioPlaying = true;
      }

      if (
        currentCut.type === 'VIDEO' &&
        (currentCut.sprite.texture.baseTexture.resource as VideoResource).source.paused &&
        (currentCut.sprite.texture.baseTexture.resource as VideoResource).source.played.length === 0
      ) {
        (currentCut.sprite.texture.baseTexture.resource as VideoResource).source.play();

        let cutIndex = this.cutIndex;
        const videoOnComplete = () => {
          setTimeout(() => {
            if (cutIndex === this.cutIndex) {
              if (this.cutIndex < this.cutSprites.length - 1) {
                this.cutIndex++;
                this.timer = 200;
              } else {
                this.emit('sceneFinished');
              }
            }
          }, 200);

          (currentCut.sprite.texture.baseTexture.resource as VideoResource).source.removeEventListener(
            'ended',
            videoOnComplete
          );
        };
        (currentCut.sprite.texture.baseTexture.resource as VideoResource).source.addEventListener(
          'ended',
          videoOnComplete,
          false
        );
      } else if (currentCut.type === 'IMAGE') {
        if (this.timer >= 0) {
          this.timer -= elapsed;
        } else if (this.cutIndex < this.cutSprites.length - 1) {
          this.cutIndex++;
          this.timer = 100;
        } else {
          this.emit('sceneFinished');
        }
      }
    }
  }

  public destroy(): void {
    for (const video of this.videos) {
      video.remove();
    }
  }
}

export default Cut;
